import { Column, Row } from 'app/design/primitives/box';
import { Button } from 'app/design/primitives/button';
import { Modal } from 'app/design/primitives/modal';
import { Text } from 'app/design/primitives/text';

import type { BottomTabNavigationOptions } from '@react-navigation/bottom-tabs';
import type { NativeStackNavigationOptions } from '@react-navigation/native-stack';
import { IconPlus, IconX } from '@tabler/icons-react-native';
import { useNavigationOptions } from 'app/hooks/navigation';
import { createModalScreenOptions } from 'app/lib/navigation';
import { useCreateManyInterests } from 'app/network/interests';
import { useAllTags, type Tag } from 'app/network/listing';
import { Box, createTheme as createDSTheme } from 'app/shopify';
import type { SpecificNavigationProp, SpecificRouteProp } from 'app/types/navigation';
import { ICON_STROKE_WIDTH } from 'app/utils/constants';
import { useRegisterLoading } from 'app/utils/performance/screen-perf-context';
import { useUISize } from 'app/utils/web-helpers';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Pressable, ScrollView, View } from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { ModalTitle } from './components/modal-title';

const theme = createDSTheme();
const CONTENT_CONTAINER_STYLE = {
  paddingHorizontal: theme.spacing['spacing.general.x5'],
  paddingBottom: theme.spacing['spacing.general.x8'],
};

function InterestTagChip({
  tagName,
  selected,
  onToggle,
}: {
  tagName: string;
  selected: boolean;
  onToggle: () => void;
}) {
  return (
    <Pressable
      onPress={onToggle}
      style={{
        padding: theme.spacing['spacing.general.x1'],
        paddingHorizontal: theme.spacing['spacing.general.x2'],
        borderRadius: theme.borderRadii['radius.xl'],
        backgroundColor: selected ? theme.colors['text.primary'] : 'white',
        borderColor: selected ? theme.colors['text.primary'] : theme.colors['border.secondary'],
        borderWidth: 1,
        marginVertical: theme.spacing['spacing.general.x0,5'],
      }}
    >
      <Row
        alignItems="center"
        style={{
          gap: theme.spacing['spacing.general.x1'],
        }}
      >
        {!selected ? <IconPlus strokeWidth={ICON_STROKE_WIDTH} size={18} color={theme.colors['text.primary']} /> : null}
        <Text style={{ fontWeight: '700', color: selected ? 'white' : theme.colors['text.primary'] }}>{tagName}</Text>
        {selected ? <IconX strokeWidth={ICON_STROKE_WIDTH} size={18} color="white" /> : null}
      </Row>
    </Pressable>
  );
}

export function useQuickInterestsScreen({
  navigation,
  route,
}: {
  navigation: SpecificNavigationProp<'quickInterests'>;
  route: SpecificRouteProp<'quickInterests'>;
}) {
  const { t } = useTranslation('quickInterest');
  const isComingFromBabillard = route.params.fromBabillard ?? false;
  const isComingFromHauteGaspesie = route.params.fromHauteGaspesie ?? false;

  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [showErrorModal, setShowErrorModal] = useState(false);

  const { data: allTagsData, isPending: isTagsPending } = useAllTags();
  const { mutate: createManyInterests, isPending: isCreatingInterests } = useCreateManyInterests();

  useRegisterLoading(isTagsPending, 'QuickInterestsScreen');

  const allTags = allTagsData ?? [];

  const handleConfirm = () => {
    if (selectedTags.length === 0) {
      setShowErrorModal(true);
      return;
    }
    createManyInterests(
      {
        type: 'interest',
        action: 'lend',
        tagIds: selectedTags,
      },
      {
        onSuccess: () => {
          if (isComingFromHauteGaspesie) {
            navigation.reset({ routes: [{ name: 'main' }] });
          } else {
            navigation.goBack();
          }
        },
        onError: () => setShowErrorModal(true),
      },
    );
  };

  const handleSkip = () => {
    if (isComingFromBabillard) {
      navigation.goBack();
    } else {
      navigation.reset({ routes: [{ name: 'main' }] });
    }
  };

  const navigationOptions = ((): NativeStackNavigationOptions => {
    const modalOptions = createModalScreenOptions(navigation, route);
    return {
      ...modalOptions,
      headerShown: false,
    };
  })();

  useNavigationOptions(navigation, navigationOptions as BottomTabNavigationOptions & NativeStackNavigationOptions);

  return {
    allTags,
    handleConfirm,
    handleSkip,
    isCreatingInterests,
    isComingFromBabillard,
    selectedTags,
    setSelectedTags,
    setShowErrorModal,
    showErrorModal,
    t,
  };
}

export type QuickInterestsScreenState = ReturnType<typeof useQuickInterestsScreen>;

function QuickInterestsBodyContent({ state }: { state: QuickInterestsScreenState }) {
  const { t } = state;
  const selectedTagIds = new Set(state.selectedTags);

  return (
    <SafeAreaView
      edges={['top', 'bottom']}
      style={{
        flex: 1,
        backgroundColor: theme.colors['surface.tertiary'],
      }}
    >
      <Column
        alignItems="center"
        justifyContent="space-between"
        marginBottom="spacing.general.x4"
        minHeight={44}
        style={{ paddingHorizontal: theme.spacing['spacing.general.x4'] }}
      >
        <ModalTitle close={state.handleSkip} title={t('headerTitle')} />
      </Column>

      <ScrollView
        keyboardShouldPersistTaps="handled"
        showsVerticalScrollIndicator={false}
        style={{ flex: 1, width: '100%' }}
        contentContainerStyle={CONTENT_CONTAINER_STYLE}
      >
        <Column style={{ gap: theme.spacing['spacing.general.x2'] }}>
          <Text
            fontSize={28}
            fontWeight="700"
            style={{
              color: theme.colors['text.primary'],
              lineHeight: 34,
              marginTop: theme.spacing['spacing.general.x2'],
            }}
          >
            {t('title')}
          </Text>
          <Text fontSize={16} style={{ color: theme.colors['text.primary'], lineHeight: 22 }}>
            {t('subtitle')}
          </Text>
          <Text
            fontSize={14}
            style={{ color: theme.colors['text.primary'], marginTop: theme.spacing['spacing.general.x3'] }}
          >
            {t('instruction')}
          </Text>
        </Column>

        <View style={{ marginTop: theme.spacing['spacing.general.x4'] }}>
          <Row style={{ flexWrap: 'wrap', gap: theme.spacing['spacing.general.x1'] }}>
            {state.allTags.map((tag: Tag) => {
              const selected = selectedTagIds.has(tag.id);
              return (
                <InterestTagChip
                  key={tag.id}
                  tagName={tag.tagName}
                  selected={selected}
                  onToggle={() => {
                    if (selected) {
                      state.setSelectedTags((prev) => prev.filter((id) => id !== tag.id));
                    } else {
                      state.setSelectedTags((prev) => [...prev, tag.id]);
                    }
                  }}
                />
              );
            })}
          </Row>
        </View>
      </ScrollView>

      <View
        style={{
          backgroundColor: theme.colors['surface.tertiary'],
          borderTopLeftRadius: theme.borderRadii['radius.xl'],
          borderTopRightRadius: theme.borderRadii['radius.xl'],
          paddingBottom: theme.spacing['spacing.general.x4'],
          paddingTop: theme.spacing['spacing.general.x2'],
          paddingHorizontal: theme.spacing['spacing.general.x5'],
        }}
      >
        <Column style={{ gap: theme.spacing['spacing.general.x2'], alignItems: 'center' }}>
          <Button
            alignSelf="center"
            disabled={state.selectedTags.length === 0 || state.isCreatingInterests}
            loading={state.isCreatingInterests}
            onPress={state.handleConfirm}
            variant="primary"
            width="100%"
          >
            <Text style={{ color: 'white', fontSize: 14, fontWeight: '700' }}>{t('button.confirm')}</Text>
          </Button>
          {!state.isComingFromBabillard ? (
            <Pressable onPress={state.handleSkip}>
              <Text
                style={{
                  color: theme.colors['text.primary'],
                  fontWeight: '700',
                  fontSize: 14,
                  textDecorationLine: 'underline',
                }}
              >
                {t('button.skip')}
              </Text>
            </Pressable>
          ) : null}
        </Column>
      </View>

      <Modal onClose={() => state.setShowErrorModal(false)} open={state.showErrorModal} transparent>
        <Modal.Card>
          <Modal.Title>
            <Text fontSize={20} fontWeight="bold" variant="bodyBase">
              {t('nothingSelectedError.title')}
            </Text>
          </Modal.Title>
          <Column style={{ marginHorizontal: theme.spacing['spacing.general.x8'] }}>
            <Text fontSize={16} textAlign="center">
              {t('nothingSelectedError.text')}
            </Text>
          </Column>
          <Button
            onPress={() => state.setShowErrorModal(false)}
            style={{ marginHorizontal: theme.spacing['spacing.general.x5'] }}
            variant="primary"
          >
            {t('nothingSelectedError.buttonText')}
          </Button>
        </Modal.Card>
      </Modal>
    </SafeAreaView>
  );
}

export function QuickInterestsScreenContent({ state }: { state: QuickInterestsScreenState }) {
  const { isLargeUI } = useUISize();
  if (!isLargeUI) {
    return <QuickInterestsBodyContent state={state} />;
  }
  return (
    <Box backgroundColor="surface.overlay" justifyContent="center" flex={1} alignItems="center">
      <QuickInterestsBodyContent state={state} />
    </Box>
  );
}
